import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { formatDateWithOptionsBs } from "@/lib/format";
import { groupOverridePeriods, type OverridePeriod, type OverrideRow } from "@/lib/override-periods";
import { Panel } from "./Panel";

type OverridesResponse = { today: string; overrides: OverrideRow[] };

const KIND_STYLES: Record<OverridePeriod["kind"], { dot: string; label: string }> = {
  off: { dot: "bg-rose-500", label: "Ne radi" },
  block: { dot: "bg-amber-500", label: "Blokirano" },
  add: { dot: "bg-emerald-500", label: "Dodatno radi" },
};

const asDate = (d: string) => new Date(d + "T00:00:00");

function daysLabel(n: number): string {
  return `${n} ${n % 10 === 1 && n % 100 !== 11 ? "dan" : "dana"}`;
}

function periodDates(p: OverridePeriod): string {
  if (p.startDate === p.endDate) {
    return formatDateWithOptionsBs(asDate(p.startDate), {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
  const from = formatDateWithOptionsBs(asDate(p.startDate), { day: "numeric", month: "short" });
  const to = formatDateWithOptionsBs(asDate(p.endDate), { day: "numeric", month: "short", year: "numeric" });
  return `${from} - ${to}`;
}

function periodDetail(p: OverridePeriod): string {
  if (p.kind === "off") return p.days > 1 ? `cijeli dan, ${daysLabel(p.days)}` : "cijeli dan";
  return p.startTime && p.endTime ? `${p.startTime}-${p.endTime}` : "";
}

/**
 * Read-only list of the vacations and calendar exceptions the therapist entered
 * themselves. Consecutive days off collapse into one line; finished periods are
 * dropped, an ongoing one keeps its real first day.
 */
export function TherapistTimeOffPanel({ therapistId }: { therapistId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "therapist", therapistId, "availability-overrides"],
    queryFn: () =>
      apiGet<OverridesResponse>(
        `/api/admin/therapists/${therapistId}/availability-overrides`,
        "Greška pri učitavanju odmora i izuzetaka",
      ),
  });

  const periods = useMemo(() => {
    if (!data) return [];
    return groupOverridePeriods(data.overrides).filter((p) => p.endDate >= data.today);
  }, [data]);

  return (
    <Panel title="Odmor i izuzeci" count={data ? periods.length : undefined} bodyClassName="px-4 py-2">
      {isLoading ? (
        <p className="py-2 text-sm text-muted-foreground">Učitavanje...</p>
      ) : periods.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground/60">Terapeut nema upisan odmor ni izuzetke u kalendaru.</p>
      ) : (
        <ul className="divide-y divide-border/50">
          {periods.map((p) => {
            const style = KIND_STYLES[p.kind];
            const ongoing = !!data && p.startDate <= data.today;
            const detail = periodDetail(p);
            return (
              <li key={`${p.kind}-${p.startDate}-${p.startTime ?? ""}`} className="flex items-start gap-2.5 py-2">
                <span className={cn("mt-1.5 size-2 shrink-0 rounded-full", style.dot)} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm tabular-nums text-foreground">{periodDates(p)}</p>
                  <p className="text-xs text-muted-foreground">
                    {style.label}
                    {detail && <span className="tabular-nums">, {detail}</span>}
                  </p>
                </div>
                {ongoing && (
                  <span className="shrink-0 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                    u toku
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
